import { useState } from 'react';
import {
    FormControl,
    Stack,
    FormLabel,
    Box,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Field } from 'formik';
import walkthroughIds from '../../../walkthroughIds';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import NumberField from 'components/forms/new/formikFormFields/NumberField';

// `type="number"` still accepts these, and they turn the input's value into an
// empty string, which would silently wipe what the user has typed.
const BLOCKED_KEYS = ['e', 'E', '+', '-', '.', ','];

/**
 * Max Channels.
 *
 * `maxAllowed` is the ceiling inherited from a parent record — a service cannot
 * exceed its service profile's value (2240).
 *
 * Out-of-range input is rejected at the keystroke, so the value can never leave
 * [minAllowed, maxAllowed]. The minimum is only enforced on validation, not
 * while typing: blocking it per-keystroke would make any minimum above 9
 * impossible to reach, since the first digit alone is always below it.
 */
const SRTMaxChannelField = ({
    darkMode,
    minAllowed = 1,
    maxAllowed = null,
    ...props
}) => {
    const validateMaxChannel = (value) => {
        if (!value) return 'Max Channels is required';
        const channels = Number(value);
        if (channels < minAllowed) {
            return `Max Channels must be at least ${minAllowed}.`;
        }
        if (maxAllowed !== null && channels > maxAllowed) {
            return `Max Channels cannot exceed the service profile limit of ${maxAllowed}.`;
        }
        return undefined;
    };

    /** Accepts a keystroke only when it leaves a whole number within range. */
    const handleChange = (form) => (e) => {
        const raw = e.target.value;
        if (raw === '') {
            form.setFieldValue('maxChannel', '');
            return;
        }
        if (!/^\d+$/.test(raw)) return;
        if (maxAllowed !== null && Number(raw) > maxAllowed) return;
        form.setFieldValue('maxChannel', raw);
    };

    const [isHovered, setIsHovered] = useState(false);
    return (
        <FormControl
            sx={{ margin: '0 !important' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Max Channels <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        {maxAllowed !== null
                            ? `Enter the maximum number of channels (${minAllowed}-${maxAllowed}).`
                            : 'Enter the maximum number of channels.'}
                    </Text>
                </Box>
                <Box flex="1" minWidth="450px">
                    <VStack width="100%" alignItems="normal" mt="15px">
                        <Field name="maxChannel" validate={validateMaxChannel}>
                            {({ field, form, meta }) => (
                                <>
                                    <NumberField
                                        field={{
                                            ...field,
                                            onChange: handleChange(form),
                                        }}
                                        customWidth="100%"
                                        id="maxChannel"
                                        min={minAllowed}
                                        max={maxAllowed ?? undefined}
                                        step={1}
                                        onKeyDown={(e) => {
                                            if (BLOCKED_KEYS.includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        data-walkthroughid={
                                            walkthroughIds.serviceProfiles
                                                ?.ufMaxChannelsInput
                                        }
                                        inputProps={{ ...props }}
                                    />
                                    {meta.touched && meta.error && (
                                        <Text
                                            color="red.500"
                                            fontSize="xs"
                                            mt={1}
                                            textAlign="right">
                                            {meta.error}
                                        </Text>
                                    )}
                                </>
                            )}
                        </Field>
                    </VStack>
                </Box>
                {isHovered && (
                    <FieldToolTip
                        label="Enter the maximum number of channels."
                        ariaLabel="srt-max-channel-tip"
                        left="25px"
                        top="37px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default SRTMaxChannelField;
